import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

interface MastermindLevel {
  id: number;
  posiciones: number;
  colores_disponibles: number;
  intentos_maximos: number;
  permite_repetidos: boolean;
  nivelNeureka: number;
  nivelNeurekaNombre: string;
  nivelNeurekaEmoji: string;
}

interface Attempt {
  colors: (number | null)[];
  hints: { black: number; white: number } | null;
  verified: boolean;
}

// Paleta de colores con emojis
const COLORS = [
  { nombre: 'Rojo', hex: '#F44336', emoji: '🔴' },
  { nombre: 'Verde', hex: '#4CAF50', emoji: '🟢' },
  { nombre: 'Azul', hex: '#2196F3', emoji: '🔵' },
  { nombre: 'Amarillo', hex: '#FFEB3B', emoji: '🟡' },
  { nombre: 'Morado', hex: '#9C27B0', emoji: '🟣' },
  { nombre: 'Naranja', hex: '#FF9800', emoji: '🟠' },
  { nombre: 'Blanco', hex: '#FFFFFF', emoji: '⚪' },
  { nombre: 'Negro', hex: '#212121', emoji: '⚫' }
];

export default function MastermindGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();

  const [level, setLevel] = useState<MastermindLevel | null>(null);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [selectedColor, setSelectedColor] = useState<number>(0);
  const [startTime] = useState(Date.now());
  const [isCompleted, setIsCompleted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [showDefeat, setShowDefeat] = useState(false);
  const [solution, setSolution] = useState<number[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [forceReload, setForceReload] = useState(0);
  const [modoAsistido, setModoAsistido] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  const dificultadOrden = parseInt(dificultad || '1');
  const { saveProgress: saveGameProgress } = useGameProgress('mastermind', dificultadOrden);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  // Medición del área de juego
  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [boardW, setBoardW] = useState(0);
  const [boardH, setBoardH] = useState(0);

  const measureBoard = () => {
    if (boardAreaRef.current) {
      const { clientWidth, clientHeight } = boardAreaRef.current;
      setBoardW(clientWidth - 8);
      setBoardH(clientHeight - 8);
    }
  };

  useEffect(() => {
    if (!loading) requestAnimationFrame(measureBoard);
  }, [loading]);

  useEffect(() => {
    window.addEventListener('resize', measureBoard);
    return () => window.removeEventListener('resize', measureBoard);
  }, []);

  // Soporte de teclado (teclas 1-8 para seleccionar colores)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (isCompleted || !level) return;

      if (e.key >= '1' && e.key <= '8') {
        const colorIdx = parseInt(e.key) - 1;
        if (colorIdx < level.colores_disponibles) {
          setSelectedColor(colorIdx);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isCompleted, level]);

  // Cargar nivel
  useEffect(() => {
    const loadLevel = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await api.get('/mastermind/level', {
          params: { dificultad: dificultadOrden }
        });

        const levelData: MastermindLevel = response.data.data;
        setLevel(levelData);

        // Inicializar intentos
        const initialAttempts: Attempt[] = Array.from(
          { length: levelData.intentos_maximos },
          () => ({
            colors: Array(levelData.posiciones).fill(null),
            hints: null,
            verified: false
          })
        );
        setAttempts(initialAttempts);
        setCurrentAttempt(0);
        setIsCompleted(false);
        setShowVictory(false);
        setShowDefeat(false);
        setSolution(null);
      } catch (err: any) {
        console.error('Error al cargar nivel:', err);
        setError(err.response?.data?.error || 'Error al cargar el nivel');
      } finally {
        setLoading(false);
      }
    };

    loadLevel();
  }, [dificultadOrden, forceReload]);

  // Colocar ficha
  const placeColor = (attemptIndex: number, position: number) => {
    if (attemptIndex !== currentAttempt || isCompleted) return;
    if (attempts[attemptIndex].verified) return;

    const newAttempts = [...attempts];
    newAttempts[attemptIndex].colors[position] = selectedColor;
    setAttempts(newAttempts);
  };

  // Verificar intento
  const verifyAttempt = async () => {
    if (!level || isCompleted) return;

    const attempt = attempts[currentAttempt];

    // Verificar que todas las posiciones estén llenas
    if (attempt.colors.some(c => c === null)) {
      setError('Completa todas las posiciones antes de verificar');
      return;
    }

    try {
      const response = await api.post('/mastermind/check', {
        levelId: level.id,
        attempt: attempt.colors,
        dificultad: dificultadOrden
      });

      const { black, white, isWin, secret } = response.data.data;

      // Actualizar intento con las pistas
      const newAttempts = [...attempts];
      newAttempts[currentAttempt].hints = { black, white };
      newAttempts[currentAttempt].verified = true;
      setAttempts(newAttempts);

      if (isWin) {
        // ¡Victoria!
        setSolution(secret);
        setIsCompleted(true);
        const tiempo = Math.floor((Date.now() - startTime) / 1000);
        await saveProgress(tiempo, currentAttempt + 1);
        setShowVictory(true);
        setError('');
      } else if (currentAttempt === level.intentos_maximos - 1) {
        // Se acabaron los intentos - derrota
        const solutionResponse = await api.post('/mastermind/reveal', {
          levelId: level.id,
          dificultad: dificultadOrden
        });
        setSolution(solutionResponse.data.data.secret);
        setIsCompleted(true);
        setShowDefeat(true);
        setError('');
      } else {
        // Siguiente intento
        setCurrentAttempt(currentAttempt + 1);
        setError('');
      }
    } catch (err: any) {
      console.error('Error al verificar intento:', err);
      setError('Error al verificar el intento');
    }
  };

  // Guardar progreso
  const saveProgress = async (tiempo: number, intentos: number) => {
    if (!level) return;

    try {
      // Guardar progreso normal (siempre)
      await saveGameProgress({
        tablero_id: level.id,
        tiempo,
        movimientos: intentos,
        completado: true,
      });

      // Si viene de una sesión, guardar también en la sesión
      await saveSessionResult({
        tiempo,
        movimientos: intentos,
        completado: true,
        datos_juego: {
          tablero_id: level.id,
          posiciones: level.posiciones,
        },
      });
    } catch (error) {
      console.error('Error al guardar progreso:', error);
    }
  };

  // Reiniciar
  const handleReset = () => {
    if (!level || !window.confirm('¿Estás seguro de que quieres reiniciar?')) return;

    const initialAttempts: Attempt[] = Array.from(
      { length: level.intentos_maximos },
      () => ({
        colors: Array(level.posiciones).fill(null),
        hints: null,
        verified: false
      })
    );
    setAttempts(initialAttempts);
    setCurrentAttempt(0);
    setIsCompleted(false);
    setShowVictory(false);
    setShowDefeat(false);
    setSolution(null);
    setError('');
  };

  // ─── Modo asistido ────────────────────────────────────────────────────────

  /** Calcula negros y blancos como si 'candidate' fuera la solución y se probara 'attempt' */
  function scoreMastermind(attempt: (number|null)[], candidate: number[]): { black: number; white: number } {
    let black = 0;
    const attRem: number[] = [];
    const canRem: number[] = [];

    for (let i = 0; i < candidate.length; i++) {
      if (attempt[i] === candidate[i]) {
        black++;
      } else {
        if (attempt[i] !== null) attRem.push(attempt[i]!);
        canRem.push(candidate[i]);
      }
    }

    let white = 0;
    const used = new Array(canRem.length).fill(false);
    for (const a of attRem) {
      for (let i = 0; i < canRem.length; i++) {
        if (!used[i] && canRem[i] === a) { white++; used[i] = true; break; }
      }
    }
    return { black, white };
  }

  /** Devuelve true si 'candidate' es consistente con todos los intentos verificados */
  function esConsistente(candidate: (number|null)[]): boolean {
    if (candidate.some(c => c === null)) return true; // incompleto, no evaluable
    const full = candidate as number[];
    for (const att of attempts) {
      if (!att.verified || !att.hints) continue;
      const score = scoreMastermind(att.colors, full);
      if (score.black !== att.hints.black || score.white !== att.hints.white) return false;
    }
    return true;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-purple-700 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Cargando...</div>
      </div>
    );
  }

  if (!level) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-purple-700 flex items-center justify-center">
        <div className="text-white text-2xl">❌ Error al cargar nivel</div>
      </div>
    );
  }

  const difficulties = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' },
    { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  return (
    <div className="h-full bg-gradient-to-br from-purple-900 to-purple-700 flex flex-col">
      {/* Banner de Sesión */}
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🎯 Mastermind</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map(d => (
            <button
              key={d.orden}
              onClick={() => {
                if (dificultadOrden === d.orden) setForceReload(prev => prev + 1);
                else navigate(`/juegos/mastermind/${d.orden}`);
              }}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dificultadOrden === d.orden ? 'bg-white text-purple-900' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} <span className="hidden sm:inline">{d.nombre}</span>
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded-md transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          <button onClick={handleReset} title="Reiniciar"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          </button>
          <button onClick={() => setForceReload(prev => prev + 1)} title="Nuevo nivel"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
          </button>
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-3 mt-2 p-2 bg-purple-100 border border-purple-300 text-purple-800 rounded-lg text-sm text-center">
          {error}
        </div>
      )}

      {/* Game area */}
      <div ref={boardAreaRef} className="flex-1 min-h-0 flex flex-col p-2">
        {boardW > 0 && boardH > 0 && (() => {
          const totalRows = level.intentos_maximos + 2; // secret + palette + attempts
          const gap = 5;
          const pad = 8;
          const rowH = Math.floor((boardH - pad * 2 - gap * (totalRows - 1)) / totalRows);
          // Circle size: fit by height and by width
          const circleFromH = Math.floor(rowH * 0.78);
          // width: number-circle + posiciones circles + verify + hints + gaps
          const circleFromW = Math.floor((boardW - pad * 2 - gap * (level.posiciones + 3)) / (level.posiciones + 3.5));
          const cs = Math.min(circleFromH, circleFromW, 64); // circle size, capped at 64px
          const fs = Math.floor(cs * 0.52);         // emoji font size
          const numCs = Math.floor(cs * 0.65);      // number circle size
          const hintS = Math.max(8, Math.floor(cs * 0.28)); // hint dot size

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap, height: boardH - pad * 2 }}>

              {/* Secret combination */}
              <div style={{
                height: rowH, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 6, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 12, padding: '0 12px', flexShrink: 0,
              }}>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginRight: 6, whiteSpace: 'nowrap' }}>
                  {solution ? '✅ Secreta' : '❓ Secreta'}
                </span>
                {Array.from({ length: level.posiciones }, (_, i) => (
                  <div key={i} style={{
                    width: cs, height: cs, borderRadius: '50%',
                    backgroundColor: solution ? COLORS[solution[i]].hex : '#888',
                    border: solution && COLORS[solution[i]].hex === '#FFFFFF' ? '3px solid #666' : '3px solid #333',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: fs, flexShrink: 0,
                  }}>
                    {solution && COLORS[solution[i]].emoji}
                  </div>
                ))}
              </div>

              {/* Color palette */}
              <div style={{
                height: rowH, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 6, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 12, padding: '0 12px', flexShrink: 0, flexWrap: 'wrap',
              }}>
                {Array.from({ length: level.colores_disponibles }, (_, i) => (
                  <button key={i} onClick={() => setSelectedColor(i)}
                    title={COLORS[i].nombre}
                    style={{
                      width: cs, height: cs, borderRadius: '50%',
                      backgroundColor: COLORS[i].hex,
                      border: selectedColor === i ? '4px solid #FBBF24' : (COLORS[i].hex === '#FFFFFF' ? '3px solid #666' : '3px solid #333'),
                      transform: selectedColor === i ? 'scale(1.15)' : 'scale(1)',
                      transition: 'all 0.15s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: fs, cursor: 'pointer', flexShrink: 0,
                    }}
                  >
                    {COLORS[i].emoji}
                  </button>
                ))}
              </div>

              {/* Attempts */}
              {attempts.map((attempt, attemptIdx) => {
                const isActive = attemptIdx === currentAttempt && !isCompleted;
                const inconsistente = modoAsistido && isActive && !esConsistente(attempt.colors);
                return (
                <div key={attemptIdx} style={{
                  height: rowH, display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px', flexShrink: 0,
                  borderRadius: 12,
                  border: `2px solid ${inconsistente ? '#ef4444' : isActive ? '#FBBF24' : 'rgba(255,255,255,0.1)'}`,
                  backgroundColor: inconsistente ? 'rgba(239,68,68,0.15)' : isActive ? 'rgba(251,191,36,0.1)' : 'rgba(255,255,255,0.05)',
                }}>
                  {/* Attempt number */}
                  <div style={{
                    width: numCs, height: numCs, borderRadius: '50%', backgroundColor: '#7C3AED',
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 'bold', fontSize: Math.floor(numCs * 0.5), flexShrink: 0,
                  }}>
                    {attemptIdx + 1}
                  </div>
                  {/* Color slots */}
                  <div style={{ display: 'flex', gap: 4, flex: 1 }}>
                    {attempt.colors.map((color, pos) => (
                      <button key={pos} onClick={() => placeColor(attemptIdx, pos)}
                        disabled={attemptIdx !== currentAttempt || isCompleted || attempt.verified}
                        style={{
                          width: cs, height: cs, borderRadius: '50%',
                          backgroundColor: color !== null ? COLORS[color].hex : '#555',
                          border: color !== null && COLORS[color].hex === '#FFFFFF' ? '3px solid #666' : '3px solid #333',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: fs,
                          cursor: attemptIdx === currentAttempt && !isCompleted && !attempt.verified ? 'pointer' : 'default',
                          transition: 'transform 0.1s', flexShrink: 0,
                        }}
                      >
                        {color !== null && COLORS[color].emoji}
                      </button>
                    ))}
                  </div>
                  {/* Verify button or hints */}
                  {!attempt.verified ? (
                    <button onClick={verifyAttempt}
                      disabled={attemptIdx !== currentAttempt || isCompleted}
                      style={{
                        padding: '4px 10px', borderRadius: 8, fontWeight: 'bold', flexShrink: 0,
                        fontSize: Math.floor(cs * 0.4),
                        backgroundColor: attemptIdx === currentAttempt && !isCompleted ? '#22c55e' : 'rgba(107,114,128,0.3)',
                        color: attemptIdx === currentAttempt && !isCompleted ? 'white' : '#9CA3AF',
                        cursor: attemptIdx === currentAttempt && !isCompleted ? 'pointer' : 'not-allowed',
                      }}
                    >
                      ✓
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', width: hintS * 2 + 6, flexShrink: 0 }}>
                      {Array.from({ length: attempt.hints!.black }, (_, k) => (
                        <div key={`b${k}`} style={{ width: hintS, height: hintS, borderRadius: '50%', backgroundColor: '#111' }} />
                      ))}
                      {Array.from({ length: attempt.hints!.white }, (_, k) => (
                        <div key={`w${k}`} style={{ width: hintS, height: hintS, borderRadius: '50%', backgroundColor: 'white', border: '1px solid #333' }} />
                      ))}
                    </div>
                  )}
                  {inconsistente && (
                    <span style={{ fontSize: Math.floor(cs * 0.4), flexShrink: 0, marginLeft: 2 }} title="Inconsistente con intentos anteriores">⚠️</span>
                  )}
                </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Mastermind"
        gameIcon="🔮"
        objective="Descubre la combinación oculta de colores en el menor número de intentos posible."
        rules={[
          'Punto negro: una ficha del color correcto está en la posición correcta',
          'Punto blanco: una ficha del color correcto está en la posición incorrecta',
          'Sin punto: ese color no aparece en la combinación',
          'Los puntos indican el total de aciertos pero no a qué posición corresponden',
        ]}
        howToPlay="Selecciona un color de la paleta y toca las posiciones del intento actual. Cuando hayas rellenado todas las posiciones, pulsa ✓ para confirmar. Usa las pistas de cada turno para deducir la combinación oculta."
      />

      {/* Modal de Victoria */}
      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{
          tiempo: Math.floor((Date.now() - startTime) / 1000),
          movimientos: currentAttempt + 1,
        }}
      />

      {/* Modal de Derrota */}
      {showDefeat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="text-6xl mb-4">😢</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">¡Se acabaron los intentos!</h2>
            <p className="text-gray-600 mb-6">La combinación secreta era:</p>
            <div className="flex justify-center gap-2 mb-6">
              {solution?.map((color, i) => (
                <div key={i} className="w-12 h-12 rounded-full border-4 flex items-center justify-center text-2xl"
                  style={{ backgroundColor: COLORS[color].hex, borderColor: COLORS[color].hex === '#FFFFFF' ? '#666' : '#333' }}>
                  {COLORS[color].emoji}
                </div>
              ))}
            </div>
            <button onClick={() => { setShowDefeat(false); setForceReload(prev => prev + 1); }}
              className="px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold text-lg transition-all">
              🎲 Intentar Otro Nivel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
